import { RegionAssignmentZod } from '@onr/schema'
import { collection, doc, type FirestoreDataConverter, GeoPoint, getFirestore, Timestamp } from 'firebase/firestore'
import { z } from 'zod'

import { ErrorWithSentryCaptureContext } from '@/sentry/ErrorWithSentryCaptureContext.ts'

const RegionCommunityZod = z.object({
  id: z.number(),
  name: z.string(),
  geolocation: z.instanceof(GeoPoint),
})

const RegionZod = z
  .object({
    name: z.string().optional().default(''),
    test: z.boolean().optional().default(false),
    mobileTransportRequestsEnabled: z.boolean().optional().default(false),
    piloteId: z.number(),
    openForTransportRequests: z.boolean().optional().default(false),
    isAutomatedAssignmentEnabled: z.boolean().default(false),
    assignment: RegionAssignmentZod.optional().default('manual'),
    currentOperatingDate: z
      .object({
        date: z.string(),
        startTime: z.string(),
        startTimestamp: z.instanceof(Timestamp).transform((v) => v.toDate()),
        endTime: z.string(),
        endTimestamp: z.instanceof(Timestamp).transform((v) => v.toDate()),
        eveningId: z.number(),
      })
      .optional(),
    waitTime: z
      .object({
        minimumInMinutes: z.number(),
        maximumInMinutes: z.number(),
        averageInMinutes: z.number(),
        exceedsThreshold: z.boolean(),
      })
      .optional(),
    waitTimeConfiguration: z.object({
      type: z.enum(['default', 'increased']).optional().default('default'),
    }),
    mapConfiguration: z
      .object({
        zoom: z.number().optional(),
        ne: z.instanceof(GeoPoint).transform((v) => ({ lat: v.latitude, lng: v.longitude })),
        sw: z.instanceof(GeoPoint).transform((v) => ({ lat: v.latitude, lng: v.longitude })),
        center: z.instanceof(GeoPoint).transform((v) => ({ lat: v.latitude, lng: v.longitude })),
      })
      .optional(),
    communities: RegionCommunityZod.array().optional().default([]),
  })
  .readonly()

export type RegionSnapshotData = z.infer<typeof RegionZod> & { _id: string }

const RegionConverter: FirestoreDataConverter<RegionSnapshotData> = {
  toFirestore: (data) => data,
  fromFirestore: (snapshot, options) => {
    const result = RegionZod.safeParse(snapshot.data(options))
    if (!result.success) {
      throw new ErrorWithSentryCaptureContext(
        `Invalid region ${snapshot.id}`,
        {
          tags: { 'firestore.zod': 'regions' },
          fingerprint: ['firestore', 'zod', 'regions'],
          extra: { data: snapshot.data(options), document: { path: snapshot.ref.path } },
        },
        { cause: result.error }
      )
    }
    return { _id: snapshot.id, ...result.data }
  },
}

export const regionDocument = (id: string) => doc(getFirestore(), 'regions', id).withConverter(RegionConverter)

export const regionCollection = () => collection(getFirestore(), 'regions').withConverter(RegionConverter)
